import { useEffect, useState, type ReactNode } from "react";
import { PROPOSED } from "../design/copy.js";
import { Pill, Rule } from "../ui/primitives.js";
import { FreeTextInput } from "../ui/input.js";

/**
 * Surfaces S1-S4 from docs/DESIGN-SYSTEM.md section 7. None of these appear
 * in the frames; each is designed here from the frames' vocabulary and
 * flagged for review rather than presented as extracted.
 */

/** S1 — recommendation submission. */
export function RecommendationComposer({
  caseName,
  engagementQuestion,
  busy,
  declined,
  onSubmit,
  onCancel,
}: {
  caseName: string;
  engagementQuestion: string;
  busy: boolean;
  declined: string | null;
  onSubmit: (text: string) => void;
  onCancel: () => void;
}): ReactNode {
  const [text, setText] = useState("");
  return (
    <main className="min-h-[calc(100svh-6rem)] bg-paper px-gutter pt-10 pb-16">
      <div className="mx-auto" style={{ width: "var(--pc-measure)" }}>
        <div className="font-sans text-micro text-ink-soft">{caseName}</div>
        <h1 className="mt-3 mb-0 font-display text-lede">{engagementQuestion}</h1>
        <Rule className="mt-6" />
        <p className="mt-6 font-sans text-small text-ink-soft">{PROPOSED.recommendationHint}</p>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={14}
          aria-label="Your recommendation"
          placeholder={`What is actually causing it, and what should ${caseName} do?`}
          className="mt-6 block w-full resize-y rounded-input border border-input-edge bg-white px-6 py-5 font-sans text-body text-ink outline-none placeholder:text-ink-faint"
        />

        {declined !== null ? <p className="mt-6 font-sans text-body">{declined}</p> : null}

        <div className="mt-8 flex gap-6">
          <Pill onClick={() => onSubmit(text)} disabled={busy || text.trim().length === 0}>
            {PROPOSED.present}
          </Pill>
          <Pill onClick={onCancel} disabled={busy}>
            not yet — back to work.
          </Pill>
        </div>
      </div>
    </main>
  );
}

/**
 * S2 — cross-examination. The same play grammar, narrowed and centred, with
 * the desk still reachable behind it. No question counter: a "3 of 5" is a
 * mechanic on screen, and the frames have no progress indicators anywhere.
 */
export function CrossExam({
  question,
  transcript,
  busy,
  onAnswer,
  onOpenSettings,
}: {
  question: string;
  transcript: readonly { speaker: string; text: string }[];
  busy: boolean;
  onAnswer: (text: string) => void;
  onOpenSettings: () => void;
}): ReactNode {
  return (
    <main className="min-h-[calc(100svh-var(--pc-grid-band))] bg-white px-gutter pt-10 pb-10">
      <div className="mx-auto flex min-h-[70svh] flex-col" style={{ width: "var(--pc-measure)" }}>
        <div className="pc-scroll flex-1">
          <h1 className="mt-0 mb-8 font-display text-title font-bold">{PROPOSED.crossExamTitle}</h1>
          {transcript.map((line, i) => (
            <p key={`${String(i)}-${line.text.slice(0, 12)}`} className="mt-0 mb-6 font-sans text-body">
              <span className="mr-3 font-display text-micro font-bold">{line.speaker}</span>
              {line.text}
            </p>
          ))}
          {question.length > 0 ? <p className="mt-8 mb-0 font-sans text-lede">{question}</p> : null}
        </div>
        <div className="mt-8">
          <FreeTextInput
            placeholder="Answer her."
            disabled={busy}
            disabledReason={busy ? PROPOSED.crossExamWaiting : undefined}
            onSubmit={onAnswer}
            onOpenSettings={onOpenSettings}
          />
        </div>
      </div>
    </main>
  );
}

/**
 * S3 — the debrief. The ONE screen where Law 2 inverts: ADR 0001 makes full
 * mechanical disclosure mandatory here, so this is the one screen allowed to
 * print a DC, a modifier and a raw roll. Nothing is collapsed by default;
 * hiding any of it would undo the point of the trade the ADR makes.
 *
 * The shape below is exactly what GET /sessions/:id/debrief returns
 * (apps/worker/src/handlers/debrief.ts writes `content`). It used to be typed
 * as `{ content: string }` and rendered `{debrief.content}` — an object as a
 * React child — so the screen threw and blanked the whole app the first time
 * a real debrief existed (2026-09-08).
 */
export interface DebriefTapeRow {
  readonly turn: number;
  readonly moveType: string;
  readonly position: string;
  readonly positionReason: string;
  readonly effect: string;
  readonly effectReason: string;
  readonly dc: number;
  readonly modifiers: readonly { readonly value: number; readonly reason: string }[];
  readonly raw: number;
  readonly modifierSum: number;
  readonly total: number;
  readonly band: string;
  /** The DC the Adjudicator proposed, where the story's ceiling was in force (ADR 0020). */
  readonly dcProposed?: number;
  readonly ceilingPhase?: string | null;
}

export interface DebriefContent {
  readonly tape: readonly DebriefTapeRow[];
  readonly counterfactuals: readonly { readonly thread: string; readonly reveal: string }[];
  readonly hiddenTruth: { readonly mechanism: string; readonly why_non_obvious: string };
  readonly decoyThesis: { readonly claim: string; readonly why_plausible: string; readonly what_disproves_it: string };
  readonly debriefAnalysis: { readonly highest_leverage_moments: readonly string[]; readonly common_wrong_turns: readonly string[]; readonly fastest_unlock_question: string };
  /** Median Assessor score per rubric dimension (1–5), or null where never scored. */
  readonly rubricSummary: Readonly<Record<string, number | null>>;
  /** The first turn on which the learner narrated a move in full, quoted (idea 10); null when they never did; absent on older debriefs. */
  readonly narratedMove?: { readonly turn: number; readonly text: string } | null;
  /** How the engagement ended (2026-09-17); absent on older debriefs, which were all presented. */
  readonly ending?: { readonly kind: "presented" } | { readonly kind: "time_expired" | "presented_at_deadline"; readonly openThreads: readonly string[] };
  /** What the difficulty retool decided (ADR 0020); absent when it never ran. */
  readonly retool?: RetoolRecord;
  /** The half-days each turn spent, a move's and a band's time charge's (rulings R1, R3, 2026-10-29); absent on older debriefs. */
  readonly time?: readonly TimeSpentRow[];
}

/** One turn's spend of the engagement's clock (packages/engine timeSpentByTurn). */
export interface TimeSpentRow {
  readonly turn: number;
  readonly move: number;
  readonly charged: number;
  readonly declared: { readonly resource: string; readonly amount: number } | null;
  /** Half-days of the spend the visible clock showed: none past the deadline (ruling R6); absent on older debriefs, read as all of it. */
  readonly shown?: number;
}

// CODEX: the debrief's words for a count of half-days, read inside the lines below ("half a day", "a day", "3 half-days").
/** Half-days in words: one is half a day, two a day. */
function halfDaysInWords(units: number): string {
  return units === 1 ? "half a day" : units === 2 ? "a day" : `${String(units)} half-days`;
}

/** The time each turn spent, as lines of evidence in turn order (Law 2; rulings R1, R3, R6). */
export function timeLines(rows: readonly TimeSpentRow[]): string[] {
  return [...rows].sort((a, b) => a.turn - b.turn).map((row) => {
    const spent = row.move + row.charged;
    const shown = row.shown ?? spent;
    const parts = [
      // CODEX: the debrief line for a move's own time, its type's half-days.
      ...(row.move > 0 ? [`the move took ${halfDaysInWords(row.move)}`] : []),
      // CODEX: the debrief line for a band's time charge, which spends one half-day whatever amount it declared.
      ...(row.charged > 0 ? [`the predicted result charged time${row.declared === null ? "" : ` (${row.declared.resource} ${String(row.declared.amount)})`}, which spends ${halfDaysInWords(row.charged)} whatever it names`] : []),
      // CODEX: the debrief line for a spend the clock did not show (ruling R6): the deadline had arrived, so the spend is recorded and no time passed on the clock, in the room or in the headings; on the turn that reached the deadline, part of it showed.
      ...(shown < spent ? [shown === 0 ? "the deadline had already arrived, so the clock showed none of it" : `the clock stopped at the deadline after ${halfDaysInWords(shown)} of it`] : []),
    ];
    return `turn ${String(row.turn).padStart(2, " ")}  ${parts.join("; ")}`;
  });
}

/** The engine's own record of how it priced and answered asks (packages/engine retool-disclosure.ts). */
export interface RetoolRecord {
  readonly asks: readonly { readonly turn: number; readonly document_id: string; readonly custody: string; readonly counterpart: string | null; readonly custodian: string | null; readonly outcome: string; readonly band: string | null; readonly due_turn: number | null; readonly fidelity: string | null }[];
  readonly people: readonly { readonly turn: number; readonly npc: string; readonly state: string; readonly form: string | null; readonly terms: readonly string[]; readonly applied: { readonly value: number; readonly reason: string } | null; readonly withheld: string | null }[];
  readonly openings: readonly { readonly turn: number; readonly planned: string; readonly fired: string | null; readonly considered: readonly string[] }[];
  readonly ceilings: readonly { readonly turn: number; readonly phase_id: string | null; readonly difficulty: string; readonly dc_proposed: number; readonly eased: boolean }[];
  /** Stage 3; absent on a debrief recorded before it. */
  /** `dropped`: a model penalty whose reason argued for the learner (D-INGRESS lifted 2026-10-27); absent before it. */
  /** `gainReplaced`: a gain naming a fact its band could not deliver, replaced by the engine (ruling 1, 2026-10-28); absent when none was. */
  /** `dayClock`: an adjudicator's tick on the day clock, dropped because only the half-day clock moves it (ruling R4, 2026-10-29); absent when none was. */
  readonly craft?: readonly { readonly turn: number; readonly withheld: readonly string[]; readonly replaced: readonly string[]; readonly dropped?: readonly string[]; readonly gainReplaced?: readonly string[]; readonly dayClock?: readonly string[] }[];
  readonly interventions?: readonly { readonly turn: number; readonly kind: "forced_route" | "nudge"; readonly detail: string }[];
  readonly story?: readonly { readonly phaseId: string; readonly enteredTurn: number; readonly beat: string | null; readonly difficulty: string | null; readonly turns: number; readonly rolledChecks: number; readonly bands: readonly string[]; readonly twist: { readonly kind: string; readonly rereads: readonly string[] } | null }[];
}

/** The DC each authored difficulty caps a check at (packages/engine structure-ceiling.ts). */
const CEILING_DC: Readonly<Record<string, number>> = { easy: 10, moderate: 15, hard: 20, very_hard: 25 };

/** The retool's record as lines of evidence, one per decision, in turn order. */
export function retoolLines(record: RetoolRecord): string[] {
  const lines: { readonly turn: number; readonly text: string }[] = [
    ...record.asks.map((ask) => ({
      turn: ask.turn,
      text: `asked ${ask.counterpart ?? "nobody by name"} for ${ask.document_id} (${ask.custody.toLowerCase()}; held by ${ask.custodian ?? "nobody"}): ${ask.outcome}${ask.band === null ? "" : ` on ${ask.band}`}${ask.fidelity === null ? "" : `, ${ask.fidelity}`}${ask.due_turn === null ? "" : `, due turn ${String(ask.due_turn)}`}`,
    })),
    ...record.people.map((person) => ({
      turn: person.turn,
      text: `${person.npc}: ${person.state}${person.form === null ? "" : ` (${person.form})`} from ${person.terms.join(", ") || "nothing"}; ${person.applied === null ? `no term${person.withheld === null ? "" : `, withheld: ${person.withheld}`}` : `${person.applied.value > 0 ? "+" : ""}${String(person.applied.value)} ${person.applied.reason}`}`,
    })),
    ...record.openings.map((opening) => ({
      turn: opening.turn,
      text: `if it missed: ${opening.planned}${opening.considered.length === 0 ? "" : ` (over ${opening.considered.join(", ")})`}; ${opening.fired === null ? "not needed" : `fired ${opening.fired}`}`,
    })),
    ...record.ceilings.map((ceiling) => ({
      turn: ceiling.turn,
      text: `story ceiling ${ceiling.difficulty}${ceiling.eased ? " (eased)" : ""} in ${ceiling.phase_id ?? "no phase"}; proposed DC ${String(ceiling.dc_proposed)}${ceiling.dc_proposed > (CEILING_DC[ceiling.difficulty] ?? Infinity) ? ". The story lowers the proposed DC here without changing the dice." : ""}`,
    })),
    ...(record.craft ?? []).flatMap((craft) => [
      ...craft.withheld.map((reason) => ({ turn: craft.turn, text: `held off the check: ${reason}` })),
      ...craft.replaced.map((reason) => ({ turn: craft.turn, text: `the engine's own term took the place of: ${reason}` })),
      // Codex's words (docs/CODEX-DRAFT-OWED-LINES-2026-10-27.md, S36): the debrief line for a model penalty dropped because its own reason argued for the learner.
      ...(craft.dropped ?? []).map((reason) => ({ turn: craft.turn, text: `the adjudicator’s penalty was set aside because its reason counts in your favor; its value was dropped, not reversed: ${reason}` })),
      // The debrief line for a band's predicted result that named a finding no paper on that band could carry, replaced by the engine's note before the roll; Codex's words, 2026-10-28.
      ...(craft.gainReplaced ?? []).map((reason) => ({ turn: craft.turn, text: `a predicted result named a finding no paper could carry, so the engine's note replaced it: ${reason}` })),
      // CODEX: the debrief line for an adjudicator's tick on the day clock, dropped at ingress (ruling R4, 2026-10-29).
      ...(craft.dayClock ?? []).map((reason) => ({ turn: craft.turn, text: `a predicted result moved the day clock itself, which only the passing half-days move, so it was dropped: ${reason}` })),
    ]),
    ...(record.interventions ?? []).map((intervention) => ({
      turn: intervention.turn,
      text: intervention.kind === "forced_route" ? `a route was forced open: ${intervention.detail}` : `the nudge ladder climbed: ${intervention.detail}`,
    })),
    ...(record.story ?? []).map((phase) => ({
      turn: phase.enteredTurn,
      text: `the story entered ${phase.phaseId}${phase.difficulty === null ? "" : ` (${phase.difficulty}${phase.beat === null ? "" : `, ${phase.beat}`})`}${phase.twist === null ? "" : `, a ${phase.twist.kind.replaceAll("_", " ")} that re-reads ${phase.twist.rereads.join(", ")}`}: ${String(phase.turns)} turn${phase.turns === 1 ? "" : "s"}, ${String(phase.rolledChecks)} rolled${phase.bands.length === 0 ? "" : `, ${phase.bands.join(" ")}`}`,
    })),
  ];
  return lines.sort((a, b) => a.turn - b.turn).map((line) => `turn ${String(line.turn).padStart(2, " ")}  ${line.text}`);
}

export interface DebriefResponse {
  readonly content: DebriefContent;
  readonly referencedAssessmentIds: readonly string[];
  readonly createdAt: string;
}

const DIMENSION_LABELS: Readonly<Record<string, string>> = {
  STRUCTURE: "structure",
  QUANTITATIVE: "quantitative",
  JUDGMENT: "judgment",
  COMMUNICATION: "communication",
  AMBIGUITY_TOLERANCE: "ambiguity tolerance",
  CALIBRATION: "calibration",
};

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="mt-12">
      <h2 className="m-0 font-display text-lede font-bold">{title}</h2>
      <Rule className="mt-3" />
      {children}
    </section>
  );
}

export function Debrief({
  debrief,
  audit,
  onOpenAudit,
  focusTurn = null,
}: {
  debrief: DebriefResponse | null;
  audit: AuditPayload | null;
  onOpenAudit: () => void;
  /** The engine turn to open at, from "view the moment" (C05); null to open at the top. */
  focusTurn?: number | null;
}): ReactNode {
  const content = debrief?.content ?? null;
  return (
    <main className="min-h-[calc(100svh-6rem)] bg-paper px-gutter pt-10 pb-16">
      <div className="mx-auto" style={{ width: "var(--pc-measure)" }}>
        <h1 className="m-0 font-display text-title font-bold">{PROPOSED.debriefTitle}</h1>
        <Rule className="mt-4" />

        {content === null ? (
          <p className="mt-8 font-sans text-body text-ink-soft">{PROPOSED.engagementWritingUp}</p>
        ) : (
          <>
            {content.ending?.kind === "time_expired" || content.ending?.kind === "presented_at_deadline" ? (
              <Section title={content.ending.kind === "time_expired" ? PROPOSED.debriefEnding : PROPOSED.debriefEndingAtDeadline}>
                <p className="mt-4 font-sans text-body">{content.ending.kind === "time_expired" ? PROPOSED.debriefEndingLead : PROPOSED.debriefEndingAtDeadlineLead}</p>
                <ul data-testid="open-threads" className="mt-4 list-none p-0 font-sans text-body text-ink-soft">
                  {content.ending.openThreads.map((line) => (
                    <li key={line} className="mb-3">
                      {line}
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}

            <Section title={PROPOSED.debriefTruth}>
              <p className="mt-4 font-sans text-lede whitespace-pre-line">{content.hiddenTruth.mechanism}</p>
              <p className="mt-4 font-sans text-body text-ink-soft whitespace-pre-line">{content.hiddenTruth.why_non_obvious}</p>
            </Section>

            <Section title={PROPOSED.debriefDecoy}>
              <p className="mt-4 font-sans text-body whitespace-pre-line">{content.decoyThesis.claim}</p>
              <p className="mt-4 font-sans text-body text-ink-soft whitespace-pre-line">{content.decoyThesis.why_plausible}</p>
              <p className="mt-4 font-sans text-body whitespace-pre-line">{content.decoyThesis.what_disproves_it}</p>
            </Section>

            <Section title={PROPOSED.debriefAnalysis}>
              <ul className="mt-4 list-none p-0 font-sans text-body">
                {content.debriefAnalysis.highest_leverage_moments.map((m) => (
                  <li key={m} className="mb-3">
                    {m}
                  </li>
                ))}
              </ul>
              <p className="mt-6 font-sans text-body">{content.debriefAnalysis.fastest_unlock_question}</p>
              <ul className="mt-4 list-none p-0 font-sans text-body text-ink-soft">
                {content.debriefAnalysis.common_wrong_turns.map((m) => (
                  <li key={m} className="mb-3">
                    {m}
                  </li>
                ))}
              </ul>
            </Section>

            <Section title={PROPOSED.debriefCounterfactual}>
              {content.counterfactuals.map((c) => (
                <div key={c.thread} className="mt-5">
                  <div className="font-sans text-small text-ink-soft">{c.thread}</div>
                  <p className="mt-1 mb-0 font-sans text-body whitespace-pre-line">{c.reveal}</p>
                </div>
              ))}
            </Section>

            {content.narratedMove !== undefined ? (
              <Section title={PROPOSED.debriefNarrated}>
                {content.narratedMove === null ? (
                  <p className="mt-4 font-sans text-body text-ink-soft">{PROPOSED.debriefNarratedNever}</p>
                ) : (
                  <>
                    <p className="mt-4 font-sans text-body text-ink-soft">{PROPOSED.debriefNarratedLead}</p>
                    <p data-testid="narrated-move" className="mt-3 max-w-[65ch] border-l-2 border-l-grid-line-major pl-4 font-sans text-body whitespace-pre-line">
                      {content.narratedMove.text}
                    </p>
                  </>
                )}
              </Section>
            ) : null}

            <Section title={PROPOSED.debriefScorecard}>
              <table className="mt-4 border-collapse font-mono text-small">
                <tbody>
                  {Object.entries(content.rubricSummary).map(([dimension, score]) => (
                    <tr key={dimension}>
                      <td className="pr-6 py-1">{DIMENSION_LABELS[dimension] ?? dimension.toLowerCase()}</td>
                      <td className="py-1">{score === null ? "—" : String(score)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            <Section title={PROPOSED.debriefRecord}>
              <TapeTable rows={content.tape} focusTurn={focusTurn} />
              <TimeSpent rows={content.time} />
              {content.retool === undefined ? null : (
                <pre className="mt-6 overflow-x-auto font-mono text-small whitespace-pre-wrap">{retoolLines(content.retool).join("\n")}</pre>
              )}
            </Section>
          </>
        )}

        {audit !== null && content === null ? (
          <section className="mt-16">
            <h2 className="m-0 font-display text-lede font-bold">{PROPOSED.debriefRecord}</h2>
            <Rule className="mt-3" />
            <CheckTape audit={audit} />
          </section>
        ) : null}

        <div className="mt-12">
          <Pill onClick={onOpenAudit}>how was I scored.</Pill>
        </div>
      </div>
    </main>
  );
}

/** Kept exported under its old name for any caller that still imports it. */
export type DebriefTape = DebriefResponse;

export interface AuditCheck {
  readonly turn: number;
  readonly moveType: string;
  readonly position: string;
  readonly positionReason: string;
  readonly effect: string;
  readonly effectReason: string;
  readonly dc: number;
  readonly modifiers: readonly { value: number; reason: string }[];
  readonly raw: number;
  readonly modifierSum: number;
  readonly total: number;
  readonly band: string;
  readonly seedCounter: number;
  readonly dcProposed?: number;
  readonly ceilingPhase?: string | null;
}

export interface AuditPayload {
  readonly sessionId: string;
  readonly events: readonly { seq: number; event: Record<string, unknown> }[];
  readonly checks: readonly AuditCheck[];
  /** The half-days each turn spent (rulings R1, R3, 2026-10-29); absent from an older server. */
  readonly time?: readonly TimeSpentRow[];
}

/** Monospace on purpose: it is a record, and it should look like one. */
function CheckTape({ audit }: { audit: AuditPayload }): ReactNode {
  return (
    <>
      <TapeTable rows={audit.checks} />
      <TimeSpent rows={audit.time} />
    </>
  );
}

/** The time each turn spent, beside the tape: nothing when no turn spent any, or the record predates it. */
function TimeSpent({ rows }: { rows: readonly TimeSpentRow[] | undefined }): ReactNode {
  if (rows === undefined || rows.length === 0) return null;
  return <pre data-testid="time-spent" className="mt-6 overflow-x-auto font-mono text-small whitespace-pre-wrap">{timeLines(rows).join("\n")}</pre>;
}

function TapeTable({ rows, focusTurn = null }: { rows: readonly DebriefTapeRow[]; focusTurn?: number | null }): ReactNode {
  // "VIEW THE MOMENT" (C05) lands the reader on the row of the turn the coach
  // pointed at. The tape is the one place the mechanics are disclosed in full
  // (Law 2), so the moment opens there, not in a coach-side summary.
  useEffect(() => {
    if (focusTurn === null) return;
    document.getElementById(`moment-turn-${String(focusTurn)}`)?.scrollIntoView({ block: "center" });
  }, [focusTurn, rows]);
  if (rows.length === 0) {
    return <p className="mt-4 font-mono text-small text-ink-soft">No check was ever specified in this session.</p>;
  }
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full border-collapse font-mono text-small">
        <thead>
          <tr className="text-left">
            {["turn", "move", "position", "effect", "DC", "modifiers", "raw", "total", "band"].map((h) => (
              <th key={h} className="border-b border-b-ink-soft px-2 py-1 font-bold whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr
              key={`${String(c.turn)}-${String(i)}`}
              id={i === rows.findIndex((r) => r.turn === c.turn) ? `moment-turn-${String(c.turn)}` : undefined}
              data-focused={focusTurn === c.turn ? "true" : undefined}
              className={focusTurn === c.turn ? "align-top bg-paper-raised" : "align-top"}
            >
              <td className="px-2 py-1">{c.turn}</td>
              <td className="px-2 py-1">{c.moveType}</td>
              <td className="px-2 py-1">
                {c.position}
                <div className="text-ink-soft">{c.positionReason}</div>
              </td>
              <td className="px-2 py-1">
                {c.effect}
                <div className="text-ink-soft">{c.effectReason}</div>
              </td>
              <td className="px-2 py-1">
                {c.dc}
                {c.dcProposed !== undefined && c.dcProposed > c.dc ? (
                  <div className="text-ink-soft">
                    proposed {c.dcProposed}; capped by {c.ceilingPhase ?? "the story"}
                  </div>
                ) : null}
              </td>
              <td className="px-2 py-1">
                {c.modifiers.length === 0 ? "—" : null}
                {c.modifiers.map((m, i) => (
                  <div key={`${String(i)}-${m.reason}`}>
                    {m.value >= 0 ? "+" : ""}
                    {m.value} {m.reason}
                  </div>
                ))}
              </td>
              <td className="px-2 py-1">{c.raw}</td>
              <td className="px-2 py-1">{c.total}</td>
              <td className="px-2 py-1 font-bold">{c.band}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** S4 — the audit tape. Deliberately unstyled: it is evidence. */
export function AuditView({ audit }: { audit: AuditPayload | null }): ReactNode {
  return (
    <main className="min-h-[calc(100svh-6rem)] bg-paper px-gutter pt-10 pb-16">
      <h1 className="m-0 font-display text-title font-bold">{PROPOSED.auditTitle}</h1>
      <Rule className="mt-4" />
      {audit === null ? (
        <p className="mt-8 font-sans text-body text-ink-soft">
          The full record opens once the engagement reaches its debrief. Nothing is withheld after that.
        </p>
      ) : (
        <>
          <CheckTape audit={audit} />
          <h2 className="mt-16 font-display text-lede font-bold">Every event, in order.</h2>
          <Rule className="mt-3" />
          <pre className="mt-4 overflow-x-auto font-mono text-small whitespace-pre-wrap">
            {audit.events.map((e) => `${String(e.seq).padStart(4, " ")}  ${JSON.stringify(e.event)}`).join("\n")}
          </pre>
        </>
      )}
    </main>
  );
}

/**
 * What the play route shows once the fourth answer is in: the engagement is
 * over and the debrief is the next thing to read. Before this the learner
 * was left with a live input box under a one-line grey status and no way to
 * the debrief at all.
 */
/**
 * `closing` is the last turn's narration when the engagement ended because its
 * days ran out (2026-09-17): the scene that says so would otherwise be replaced
 * by this screen before it could be read.
 */
export function EngagementOver({ onOpenDebrief, closing = [] }: { onOpenDebrief: () => void; closing?: readonly string[] }): ReactNode {
  return (
    <main className="min-h-[calc(100svh-var(--pc-grid-band))] bg-white px-gutter pt-10 pb-10">
      <div className="mx-auto" style={{ width: "var(--pc-measure)" }}>
        {closing.map((text) => (
          <p key={text} data-testid="closing-narration" className="mt-0 mb-6 font-sans text-body whitespace-pre-line">
            {text}
          </p>
        ))}
        <h1 className="m-0 font-display text-title font-bold">{PROPOSED.engagementComplete}</h1>
        <Rule className="mt-4" />
        <p className="mt-6 font-sans text-body">{PROPOSED.engagementWritingUp}</p>
        <div className="mt-8">
          <Pill onClick={onOpenDebrief}>{PROPOSED.toDebrief}</Pill>
        </div>
      </div>
    </main>
  );
}
